import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from '../../../config/axios'
import { Task, CreateTaskPayload, TeamMemberTask, User } from './types'
import { TeamMemberInfo } from '../../auth/types'

interface FetchAssignedTasksPayload {
  taskIds?: string[]
  teamMemberEmail?: string
  teamMemberWorkdayId?: string
}

export interface FetchAssignedTaskDetailsPayload {
  teamMemberEmail: string
  teamMemberWorkdayId: string
}

// Async thunk for fetching task
export const fetchAssignedTasksByFilters = createAsyncThunk<TeamMemberTask[], FetchAssignedTasksPayload>(
  'tasks/fetchAssignedTasksByFilters',
  async (filters) => {
    try {
      const response = await axios.post('/task/assignedTask', filters, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      console.log('redux/task/actions:', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching assigned tasks:', error)
      throw new Error('Failed to fetch tasks')
    }
  },
)

// Async thunk for fetching assigned task details
export const fetchAssignedTaskDetails = createAsyncThunk<Task[], FetchAssignedTaskDetailsPayload>(
  'task/fetchAssignedTaskDetails',
  async (payload) => {
    try {
      const { teamMemberEmail, teamMemberWorkdayId } = payload
      const response = await axios.get('/task/getAssignedTaskDetails', {
        params: {
          teamMemberEmail,
          teamMemberWorkdayId,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('Failed to fetch assigned task details:', error.response?.data || error.message)
      throw error // Forward error to thunk's error handling
    }
  },
)

// Async thunk for updating an existing event
export const updateTask = createAsyncThunk(
  'task/updateTask',
  async (
    payload: {
      id: number
      title: string
      dueDate: Date | null
      details: string
      link: string
      importance: string
      status: string
      createdBy: string
    },
    { getState },
  ) => {
    try {
      const state = getState() as any
      const user = state.auth.user.teamMemberInfo as TeamMemberInfo

      const { id, ...data } = payload
      const jsonPayload = {
        id,
        ...data,
        dueDate: data.dueDate ? data.dueDate : null,
        updatedAt: new Date(),
        updatedBy: {
          email: user.email,
          workdayId: user.workdayId,
        },
      }

      const response = await axios.patch('/task/updateTask', jsonPayload)
      return response.data
    } catch (error: any) {
      console.error('Failed to update task:', error.response?.data || error.message)
      throw error // Forward error to thunk's error handling
    }
  },
)

interface FetchTasksPayload {
  taskIds?: string[]
  search?: string
  Status?: string[]
  Importance?: string[]
}

// Async thunk for fetching task
export const fetchTasksByFilters = createAsyncThunk<Task[], FetchTasksPayload>(
  'tasks/fetchTasksByFilters',
  async (filters) => {
    try {
      const response = await axios.post('/task', filters, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error) {
      console.error('Error fetching tasks:', error)
      throw new Error('Failed to fetch tasks')
    }
  },
)

export const fetchTasks = createAsyncThunk<Task[]>(
  'manageTasks/fetchTasks',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post('/task')
      return response.data
    } catch (error) {
      return rejectWithValue('Failed to fetch tasks')
    }
  },
)

export const createTask = createAsyncThunk<Task, CreateTaskPayload>(
  'task/createTask',
  async (taskData, { getState }) => {
    try {
      const state = getState() as any
      const user = state.auth.user.teamMemberInfo as TeamMemberInfo

      const { title, dueDate, details, link, importance, status } = taskData
      const jsonPayload = {
        title,
        dueDate: dueDate ? dueDate : null,
        details,
        link,
        importance,
        status,
        createdBy: {
          email: user.email,
          workdayId: user.workdayId,
        },
      }

      const response = await axios.post('/task/createTask', jsonPayload)
      return response.data
    } catch (error: any) {
      console.error('Failed to create task:', error.response?.data || error.message)
      throw error // Forward error to thunk's error handling
    }
  },
)

export const updateAssignedTask = createAsyncThunk(
  'task/updateTask',
  async (payload: any, { rejectWithValue }) => {
    const { taskId, teamMemberEmail, teamMemberWorkdayId, status } = payload

    const queryParams = new URLSearchParams()
    queryParams.append('taskId', taskId.toString())
    queryParams.append('teamMemberEmail', teamMemberEmail)
    queryParams.append('teamMemberWorkdayId', teamMemberWorkdayId)

    const formData = new FormData()
    if (status) formData.append('status', status)

    try {
      const response = await axios.patch(`/task/updateAssignedTask?${queryParams}`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('Failed to update task:', error)
      return rejectWithValue(error.response?.data || error.message)
    }
  },
)

// Async thunk for archiving an existing task
export const updateArchiveTask = createAsyncThunk(
  'task/updateTask',
  async (payload: { id: number; updatedBy: User } & Partial<Event>, { rejectWithValue }) => {
    const { id, updatedBy, ...data } = payload

    if (!id) {
      return rejectWithValue('Task ID is required.')
    }

    const jsonPayload = {
      id,
      ...data,
      updatedAt: new Date(), // Add the updatedAt timestamp
      updatedBy: {
        email: updatedBy.email,
        workdayId: updatedBy.workdayId,
      },
    }

    try {
      const response = await axios.patch('/task/updateTask', jsonPayload)
      return response.data
    } catch (error: any) {
      console.error('Failed to update task:', error.response?.data || error.message)
      return rejectWithValue(error.response?.data?.message || 'Failed to update task.')
    }
  },
)
